import { getSupabase, getSession, getUserId } from './supabase-client.js';
import { canEdit } from './store.js';
import { parseCoordsFromMapsUrl } from './utils/map-coords.js';

function assertCanEdit() {
  if (!canEdit()) {
    const err = new Error(
      navigator.onLine
        ? 'Non hai permesso di modificare questo viaggio'
        : 'Modifiche non disponibili offline',
    );
    if (!navigator.onLine) err.name = 'OfflineError';
    throw err;
  }
}

function groupStopsByDay(stops, dayKey = 'instance_day_id') {
  const map = new Map();
  for (const stop of stops ?? []) {
    const list = map.get(stop[dayKey]) ?? [];
    list.push(stop);
    map.set(stop[dayKey], list);
  }
  for (const list of map.values()) {
    list.sort((a, b) => a.order_idx - b.order_idx);
  }
  return map;
}

function attachStopsToDays(days, stops) {
  const byDay = groupStopsByDay(stops);
  return (days ?? [])
    .slice()
    .sort((a, b) => a.order_idx - b.order_idx)
    .map((day) => ({
      ...day,
      stops: (byDay.get(day.id) ?? []).filter((s) => !s.hidden),
    }));
}

export function normalizeBase(row) {
  if (!row) return null;
  return {
    id: row.id,
    name: row.name,
    subtitle: row.subtitle,
    dateStart: row.date_start,
    dateEnd: row.date_end,
    mapsUrl: row.maps_url,
    orderIdx: row.order_idx,
    lat: row.lat ?? null,
    lng: row.lng ?? null,
    placeId: row.place_id ?? null,
    costEur: row.cost_eur != null ? Number(row.cost_eur) : null,
    expenseId: row.expense_id ?? null,
  };
}

function parseOptionalCost(value) {
  if (value === undefined || value === null) return undefined;
  const raw = String(value).trim();
  if (!raw) return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) throw new Error('Importo alloggio non valido');
  return Math.round(n * 100) / 100;
}

async function syncBaseExpense(instanceId, baseId, { costEur, expenseId, dateStart, name }, userId) {
  const supabase = getSupabase();
  const label = `Alloggio — ${name}`;
  const dayKey = dateStart;
  const now = new Date().toISOString();

  if (costEur == null) {
    if (expenseId) {
      const { error: delErr } = await supabase.from('expenses').delete().eq('id', expenseId);
      if (delErr) throw delErr;
    }
    const { error: baseErr } = await supabase
      .from('instance_bases')
      .update({ expense_id: null, cost_eur: null, updated_at: now, updated_by: userId })
      .eq('id', baseId)
      .eq('instance_id', instanceId);
    if (baseErr) throw baseErr;
    return null;
  }

  if (expenseId) {
    const { error: expErr } = await supabase
      .from('expenses')
      .update({
        label,
        amount_eur: costEur,
        day_key: dayKey,
        category: 'alloggio',
        updated_by: userId,
        updated_at: now,
      })
      .eq('id', expenseId);
    if (expErr) throw expErr;

    const { error: baseErr } = await supabase
      .from('instance_bases')
      .update({ cost_eur: costEur, updated_at: now, updated_by: userId })
      .eq('id', baseId)
      .eq('instance_id', instanceId);
    if (baseErr) throw baseErr;
    return expenseId;
  }

  const { data: expense, error: insErr } = await supabase
    .from('expenses')
    .insert({
      instance_id: instanceId,
      day_key: dayKey,
      label,
      amount_eur: costEur,
      category: 'alloggio',
      updated_by: userId,
      updated_at: now,
    })
    .select('id')
    .single();
  if (insErr) throw insErr;

  const { error: linkErr } = await supabase
    .from('instance_bases')
    .update({
      expense_id: expense.id,
      cost_eur: costEur,
      updated_at: now,
      updated_by: userId,
    })
    .eq('id', baseId)
    .eq('instance_id', instanceId);
  if (linkErr) throw linkErr;
  return expense.id;
}

async function requireUserId() {
  const session = await getSession();
  const userId = getUserId(session);
  if (!userId) throw new Error('Sessione non valida');
  return userId;
}

export async function loadInstanceBases(instanceId) {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('instance_bases')
    .select('*')
    .eq('instance_id', instanceId)
    .order('order_idx');
  if (error) throw error;
  return (data ?? []).map(normalizeBase);
}

export async function loadInstanceItinerary(instanceId) {
  const supabase = getSupabase();
  const { data: days, error: daysError } = await supabase
    .from('instance_days')
    .select('*')
    .eq('instance_id', instanceId)
    .order('order_idx');
  if (daysError) throw daysError;
  if (!days?.length) return null;

  const dayIds = days.map((d) => d.id);
  const { data: stops, error: stopsError } = await supabase
    .from('instance_stops')
    .select('*')
    .in('instance_day_id', dayIds)
    .order('order_idx');
  if (stopsError) throw stopsError;

  return attachStopsToDays(days, stops ?? []);
}

/** Copy plan days/stops into instance tables on first access. */
export async function ensureInstanceItinerary(instanceId, planDays) {
  const existing = await loadInstanceItinerary(instanceId);
  if (existing?.length) return existing;

  if (!navigator.onLine) {
    return planDays;
  }

  const supabase = getSupabase();
  const userId = await requireUserId();
  const now = new Date().toISOString();

  for (const day of planDays) {
    const { data: instDay, error: dayError } = await supabase
      .from('instance_days')
      .insert({
        instance_id: instanceId,
        plan_day_id: day.id,
        date: day.date,
        day_type: day.day_type,
        title: day.title,
        ritmo: day.ritmo,
        order_idx: day.order_idx,
        updated_by: userId,
        updated_at: now,
      })
      .select()
      .single();
    if (dayError) throw dayError;

    for (const stop of day.stops ?? []) {
      const { error: stopError } = await supabase.from('instance_stops').insert({
        instance_id: instanceId,
        instance_day_id: instDay.id,
        plan_stop_id: stop.id,
        name: stop.name,
        car_time: stop.car_time,
        walk_info: stop.walk_info,
        cost_info: stop.cost_info,
        maps_url: stop.maps_url,
        lat: stop.lat ?? null,
        lng: stop.lng ?? null,
        place_id: stop.place_id ?? null,
        warning: stop.warning,
        description: stop.description,
        image_url: stop.image_url,
        order_idx: stop.order_idx,
        hidden: false,
        updated_by: userId,
        updated_at: now,
      });
      if (stopError) throw stopError;
    }
  }

  return loadInstanceItinerary(instanceId);
}

export async function resolveItineraryDays(instanceId, planDays) {
  if (!navigator.onLine) {
    return planDays;
  }

  const fromInstance = await loadInstanceItinerary(instanceId);
  if (fromInstance?.length) return fromInstance;
  return ensureInstanceItinerary(instanceId, planDays);
}

// ---------------------------------------------------------------------------
// CRUD — instance bases
// ---------------------------------------------------------------------------

export async function upsertInstanceBase(instanceId, base) {
  assertCanEdit();
  const userId = await requireUserId();
  const supabase = getSupabase();
  const now = new Date().toISOString();
  const costEur = parseOptionalCost(base.costEur);

  const row = {
    instance_id: instanceId,
    name: base.name,
    subtitle: base.subtitle ?? null,
    date_start: base.dateStart,
    date_end: base.dateEnd,
    maps_url: base.mapsUrl ?? null,
    lat: base.lat ?? null,
    lng: base.lng ?? null,
    order_idx: base.orderIdx ?? 0,
    updated_by: userId,
    updated_at: now,
  };

  let saved;
  if (base.id) {
    const { data, error } = await supabase
      .from('instance_bases')
      .update(row)
      .eq('id', base.id)
      .eq('instance_id', instanceId)
      .select()
      .single();
    if (error) throw error;
    saved = normalizeBase(data);
  } else {
    const { data, error } = await supabase.from('instance_bases').insert(row).select().single();
    if (error) throw error;
    saved = normalizeBase(data);
  }

  if (base.costEur !== undefined) {
    await syncBaseExpense(
      instanceId,
      saved.id,
      {
        costEur: costEur ?? null,
        expenseId: base.expenseId ?? saved.expenseId,
        dateStart: saved.dateStart,
        name: saved.name,
      },
      userId,
    );
    const { data: refreshed, error: refErr } = await supabase
      .from('instance_bases')
      .select('*')
      .eq('id', saved.id)
      .single();
    if (refErr) throw refErr;
    return normalizeBase(refreshed);
  }

  return saved;
}

export async function deleteInstanceBase(instanceId, baseId) {
  assertCanEdit();
  const supabase = getSupabase();

  const { data: base, error: findErr } = await supabase
    .from('instance_bases')
    .select('expense_id')
    .eq('id', baseId)
    .eq('instance_id', instanceId)
    .maybeSingle();
  if (findErr) throw findErr;

  if (base?.expense_id) {
    const { error: expErr } = await supabase.from('expenses').delete().eq('id', base.expense_id);
    if (expErr) throw expErr;
  }

  const { error } = await supabase
    .from('instance_bases')
    .delete()
    .eq('id', baseId)
    .eq('instance_id', instanceId);
  if (error) throw error;
}

// ---------------------------------------------------------------------------
// CRUD — instance days & stops
// ---------------------------------------------------------------------------

export async function updateInstanceDay(instanceId, dayId, patch) {
  assertCanEdit();
  const userId = await requireUserId();
  const supabase = getSupabase();
  const now = new Date().toISOString();

  const row = { updated_by: userId, updated_at: now };
  if (patch.title !== undefined) row.title = patch.title;
  if (patch.ritmo !== undefined) row.ritmo = patch.ritmo;
  if (patch.dayType !== undefined) row.day_type = patch.dayType;

  const { data, error } = await supabase
    .from('instance_days')
    .update(row)
    .eq('id', dayId)
    .eq('instance_id', instanceId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function upsertInstanceStop(instanceId, instanceDayId, stop) {
  assertCanEdit();
  const userId = await requireUserId();
  const supabase = getSupabase();
  const now = new Date().toISOString();

  const row = {
    instance_id: instanceId,
    instance_day_id: instanceDayId,
    name: stop.name,
    car_time: stop.carTime ?? null,
    walk_info: stop.walkInfo ?? null,
    cost_info: stop.costInfo ?? null,
    maps_url: stop.mapsUrl ?? null,
    warning: stop.warning ?? null,
    description: stop.description ?? null,
    image_url: stop.imageUrl ?? null,
    lat: stop.lat ?? null,
    lng: stop.lng ?? null,
    place_id: stop.placeId ?? null,
    place_meta: stop.placeMeta ?? {},
    scheduled_at: stop.scheduledAt ?? null,
    order_idx: stop.orderIdx ?? 0,
    hidden: stop.hidden ?? false,
    updated_by: userId,
    updated_at: now,
  };

  if (stop.id) {
    const { data, error } = await supabase
      .from('instance_stops')
      .update(row)
      .eq('id', stop.id)
      .eq('instance_id', instanceId)
      .select()
      .single();
    if (error) throw error;
    return data;
  }

  const { data, error } = await supabase.from('instance_stops').insert(row).select().single();
  if (error) throw error;
  return data;
}

export async function deleteInstanceStop(instanceId, stopId) {
  assertCanEdit();
  const supabase = getSupabase();
  const { error } = await supabase
    .from('instance_stops')
    .delete()
    .eq('id', stopId)
    .eq('instance_id', instanceId);
  if (error) throw error;
}

export async function updateInstanceStopCoords(instanceId, stopId, { lat, lng, placeId = null }) {
  if (!navigator.onLine) return;
  assertCanEdit();
  const supabase = getSupabase();
  const { error } = await supabase
    .from('instance_stops')
    .update({
      lat,
      lng,
      place_id: placeId,
      updated_at: new Date().toISOString(),
    })
    .eq('id', stopId)
    .eq('instance_id', instanceId);
  if (error) throw error;
}

export async function updateInstanceBaseCoords(instanceId, baseId, { lat, lng, placeId = null }) {
  if (!navigator.onLine) return;
  assertCanEdit();
  const supabase = getSupabase();
  const { error } = await supabase
    .from('instance_bases')
    .update({
      lat,
      lng,
      place_id: placeId,
      updated_at: new Date().toISOString(),
    })
    .eq('id', baseId)
    .eq('instance_id', instanceId);
  if (error) throw error;
}

export async function reorderInstanceStops(instanceId, instanceDayId, orderedStopIds) {
  assertCanEdit();
  const userId = await requireUserId();
  const supabase = getSupabase();
  const now = new Date().toISOString();

  await Promise.all(
    orderedStopIds.map((stopId, index) =>
      supabase
        .from('instance_stops')
        .update({ order_idx: index, updated_by: userId, updated_at: now })
        .eq('id', stopId)
        .eq('instance_id', instanceId)
        .eq('instance_day_id', instanceDayId),
    ),
  ).then((results) => {
    const err = results.find((r) => r.error)?.error;
    if (err) throw err;
  });
}

// ---------------------------------------------------------------------------
// Wishlist — luoghi non schedulati
// ---------------------------------------------------------------------------

export function normalizeWishlistItem(row) {
  if (!row) return null;
  return {
    id: row.id,
    name: row.name,
    notes: row.notes,
    lat: row.lat,
    lng: row.lng,
    placeId: row.place_id,
    placeMeta: row.place_meta ?? {},
    mapsUrl: row.maps_url,
    showOnMap: row.show_on_map !== false,
    orderIdx: row.order_idx,
  };
}

export async function loadInstanceWishlist(instanceId) {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('instance_wishlist')
    .select('*')
    .eq('instance_id', instanceId)
    .order('order_idx');
  if (error) throw error;
  return (data ?? []).map(normalizeWishlistItem);
}

export async function upsertWishlistItem(instanceId, item) {
  assertCanEdit();
  const userId = await requireUserId();
  const supabase = getSupabase();
  const now = new Date().toISOString();

  const row = {
    instance_id: instanceId,
    name: item.name,
    notes: item.notes ?? null,
    lat: item.lat ?? null,
    lng: item.lng ?? null,
    place_id: item.placeId ?? null,
    place_meta: item.placeMeta ?? {},
    maps_url: item.mapsUrl ?? null,
    show_on_map: item.showOnMap !== false,
    order_idx: item.orderIdx ?? 0,
    updated_by: userId,
    updated_at: now,
  };

  if (item.id) {
    const { data, error } = await supabase
      .from('instance_wishlist')
      .update(row)
      .eq('id', item.id)
      .eq('instance_id', instanceId)
      .select()
      .single();
    if (error) throw error;
    return normalizeWishlistItem(data);
  }

  const { data, error } = await supabase.from('instance_wishlist').insert(row).select().single();
  if (error) throw error;
  return normalizeWishlistItem(data);
}

export async function deleteWishlistItem(instanceId, itemId) {
  assertCanEdit();
  const supabase = getSupabase();
  const { error } = await supabase
    .from('instance_wishlist')
    .delete()
    .eq('id', itemId)
    .eq('instance_id', instanceId);
  if (error) throw error;
}

export async function reorderWishlist(instanceId, orderedIds) {
  assertCanEdit();
  const userId = await requireUserId();
  const supabase = getSupabase();
  const now = new Date().toISOString();

  await Promise.all(
    orderedIds.map((id, index) =>
      supabase
        .from('instance_wishlist')
        .update({ order_idx: index, updated_by: userId, updated_at: now })
        .eq('id', id)
        .eq('instance_id', instanceId),
    ),
  ).then((results) => {
    const err = results.find((r) => r.error)?.error;
    if (err) throw err;
  });
}

/** One-shot: persist lat/lng parsed from Google Maps URLs (no Nominatim). */
export async function backfillInstanceCoordsFromUrls(instanceId) {
  if (!navigator.onLine) return 0;

  const itinerary = await loadInstanceItinerary(instanceId);
  const bases = await loadInstanceBases(instanceId);
  let updated = 0;

  for (const day of itinerary ?? []) {
    for (const stop of day.stops ?? []) {
      if (stop.lat && stop.lng) continue;
      const coords = parseCoordsFromMapsUrl(stop.maps_url);
      if (!coords) continue;
      try {
        await updateInstanceStopCoords(instanceId, stop.id, coords);
        stop.lat = coords.lat;
        stop.lng = coords.lng;
        updated += 1;
      } catch {
        /* viewer or offline */
      }
    }
  }

  for (const base of bases) {
    if (base.lat && base.lng) continue;
    const coords = parseCoordsFromMapsUrl(base.mapsUrl);
    if (!coords) continue;
    try {
      await updateInstanceBaseCoords(instanceId, base.id, coords);
      base.lat = coords.lat;
      base.lng = coords.lng;
      updated += 1;
    } catch {
      /* viewer or offline */
    }
  }

  return updated;
}
